import _ from "lodash";
import React, { useEffect } from "react";
import { connect } from "react-redux";
import { compose, graphql } from "lib/apolloClient";

import { getStaffCurriculumProgramsQuery } from "AppComponents/CourseSelection/modules/Queries";
import { getStaffCurriculumProgramsQueryFromCache } from "AppComponents/CourseSelection/modules/GraphqlHelpers";
import { getCurriculumOptionsMemoized } from "AppComponents/CourseSelection/modules/Utils";
import {
  showDPLibraryForUBD,
  getCurriculumProgramsWithAllClassAccess,
  isLEorUnitPlanView,
} from "ToddleLibrary/modules/ToddleLibraryUtils";
import { setCurrentCurriculumProgram } from "Platform/modules/PlatformModule";
import classes from "./CurriculumList.scss";

import withLoader from "UIComponents/GqlWithLoader";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { useI18n } from "Hooks";
import {
  CURRICULUM_TYPE_PYP,
  CURRICULUM_TYPE_UBD,
  CURRICULUM_TYPE_DP,
  CURRICULUM_TYPE_MYP,
} from "Constants/stringConstants";
import { MYP_GRADE_LABELS } from "ToddleLibrary/modules/Constants";

import { SideNavigation } from "@toddle-edu/ds-web";
import { getIsLearningCoursesEnabled } from "Teacher/routes/LearningCourses/modules/Utils";

const styles = {
  navigationContainer: {
    height: "100%",
    width: "272px",
  },
};

const CurriculumList = ({
  curriculumPrograms,
  selectedCurriulum,
  onCurriculumChange,
  setCurrentCurriculumProgram,
  shouldSetPlatformCurriculumOnMount = true,
  isLoading,
  curriculumsLoaded,
}) => {
  const { t } = useI18n();
  const {
    curriculumOptions: sideNavOptions,
    defaultOption,
  } = getCurriculumOptionsMemoized({ curriculumPrograms, t });

  const updateSelectedCurriculum = id => {
    const selectedCurriulum = _.find(curriculumPrograms, { id });
    onCurriculumChange(selectedCurriulum);
  };

  useEffect(() => {
    const curriculumId = defaultOption.key;
    const curriculumProgram = _.find(curriculumPrograms, { id: curriculumId });

    updateSelectedCurriculum(defaultOption.key);
    // For Toddle Library, it’s set to false to avoid curriculum reset on mount and prevent flicker (on clicking "Use in class") for unit plans.
    if (shouldSetPlatformCurriculumOnMount) {
      setCurrentCurriculumProgram(curriculumProgram);
    }
  }, [sideNavOptions, defaultOption, shouldSetPlatformCurriculumOnMount]);

  useEffect(() => {
    if (!isLoading) {
      curriculumsLoaded();
    }
  }, [isLoading]);

  const handleSideNavOptionChange = key => {
    updateSelectedCurriculum(key);
  };

  return (
    <div className={classes.curriculumListContainer}>
      <SideNavigation
        dsVersion="2.0"
        options={sideNavOptions}
        theme="subtle"
        selectedOptionKey={_.get(selectedCurriulum, "id")}
        containerStyle={styles.navigationContainer}
        onClick={handleSideNavOptionChange}
        testId="appComponents-courseSelection-curriculumList"
      />
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const { academicYears } = ownProps;
  const platformUserInfo = getUserInfo({ portalType: "PLANNER" });
  const userId = platformUserInfo.id;
  const globalFilter = _.get(state, "toddleLibrary.globalFilter", {});

  const curriculumProgramsWithAllClassAccess = getCurriculumProgramsWithAllClassAccess(
    state
  );

  const isToddleLibrary = state.login.activeTab == "TODDLE_LIBRARY";
  const isLearningCoursesEnabled = getIsLearningCoursesEnabled();
  const globalGradeLabel = _.get(globalFilter, "grade.label", "");
  const isDPLibrary = _.isEqual(globalGradeLabel, "Grade 11 & 12");
  const isMypGrade = _.includes(MYP_GRADE_LABELS, globalGradeLabel);

  const staffCurriculumProgramsQueryVariables = {
    id: userId,
    portalType: "PLANNER", // Need to pass this to send platform user JWT in request header
  };

  if (!_.isEmpty(academicYears)) {
    staffCurriculumProgramsQueryVariables["userCurriculumProgramFilters"] = {
      academicYearId: _.first(academicYears),
    };
  }

  return {
    userId,
    staffCurriculumProgramsQueryVariables,
    curriculumProgramsWithAllClassAccess,
    isToddleLibrary,
    isDPLibrary,
    isMypGrade,
    isLearningCoursesEnabled,
    isData: true,
    isLoading: false,
  };
};

const mapActionCreators = { setCurrentCurriculumProgram };

export default compose(
  connect(mapStateToProps, mapActionCreators),
  graphql(getStaffCurriculumProgramsQuery, {
    name: "getStaffCurriculumPrograms",
    skip: ({ userId }) => !userId,
    options: ({ staffCurriculumProgramsQueryVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: staffCurriculumProgramsQueryVariables,
    }),
    props({
      getStaffCurriculumPrograms,
      ownProps: {
        staffCurriculumProgramsQueryVariables,
        isData,
        isLoading,
        isToddleLibrary,
        isDPLibrary,
        isMypGrade,
        curriculumProgramsWithAllClassAccess,
      },
    }) {
      const queryData = getStaffCurriculumProgramsQueryFromCache(
        staffCurriculumProgramsQueryVariables
      );
      const staffCurriculumPrograms = _.get(queryData, "curriculumPrograms");

      //show only school type programs for toddle library
      const filteredCurriculumPrograms = _.filter(
        staffCurriculumPrograms,
        ({ programType }) => {
          if (isToddleLibrary) {
            return programType === "SCHOOL";
          }
          return true;
        }
      );

      const allCurriculumPrograms = _.unionBy(
        filteredCurriculumPrograms,
        curriculumProgramsWithAllClassAccess,
        "id"
      );

      const curriculumPrograms = _.filter(allCurriculumPrograms, ({ type }) => {
        if (isToddleLibrary) {
          if (isDPLibrary) {
            const allowedTypes = showDPLibraryForUBD()
              ? [CURRICULUM_TYPE_DP, CURRICULUM_TYPE_UBD]
              : [CURRICULUM_TYPE_DP];
            return _.includes(allowedTypes, type);
          }
          if (isMypGrade && !isLEorUnitPlanView()) {
            return type === CURRICULUM_TYPE_MYP;
          }
          return _.includes(
            [CURRICULUM_TYPE_PYP, CURRICULUM_TYPE_UBD, CURRICULUM_TYPE_MYP],
            type
          );
        }
        return true;
      });

      return {
        isData: !_.isEmpty(curriculumPrograms) && isData,
        isLoading:
          isLoading ||
          _.includes([1, 2], getStaffCurriculumPrograms.networkStatus),
        curriculumPrograms,
      };
    },
  }),
  withLoader
)(CurriculumList);
